import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import FNMSponsorDashboard from './FNMSponsorDashboard';

const ProtectedApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Demo credentials - change these!
  const DEMO_PASSWORD = 'FNM2025DEMO';
  const DEMO_EXPIRY = '2025-12-01';
  
  useEffect(() => {
    // Check if already authenticated
    const auth = sessionStorage.getItem('fnm-authenticated');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
    
    // Check if demo has expired
    if (new Date() > new Date(DEMO_EXPIRY)) {
      sessionStorage.removeItem('fnm-authenticated');
      setIsAuthenticated(false);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    
    if (password === DEMO_PASSWORD) {
      sessionStorage.setItem('fnm-authenticated', 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid access code. Please contact FNM for demo access.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('fnm-authenticated');
    setIsAuthenticated(false);
  };

  // Check expiry
  if (new Date() > new Date(DEMO_EXPIRY)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Demo Expired</h2>
          <p className="text-gray-600">This demonstration has expired. Please contact FNM for access.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">FNM</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">FNM Sponsor Dashboard</h1>
            <p className="text-gray-600">Private Demo Access</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Code
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 pr-10"
                  placeholder="Enter demo access code"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-colors"
            >
              Access Dashboard
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">🔒 Confidential Demo</h3>
              <p className="text-xs text-gray-600">
                This is a private demonstration of FNM's sponsor dashboard technology. 
                Unauthorized access is prohibited.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Confidential Watermark */}
      <div className="fixed top-4 right-4 z-50 bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-bold opacity-80">
        CONFIDENTIAL DEMO
      </div>
      
      {/* Logout Button */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={handleLogout}
          className="bg-gray-800 text-white px-3 py-1 rounded-lg text-xs hover:bg-gray-700"
        >
          End Session
        </button>
      </div>
      
      <FNMSponsorDashboard />
    </div>
  );
};

export default ProtectedApp;